import React, { useEffect, useRef } from "react";
import { getVintedCategoryIcon } from "./helpers";

// Helper function to generate default Vinted description
const getDefaultVintedDescription = (formData) => {
  const fields = [];

  if (formData.brand) fields.push(`Brand: ${formData.brand}`);
  const size = formData.vintedSize || formData.variants?.[0]?.size;
  if (size) fields.push(`Size: ${size}`);
  if (formData.color) fields.push(`Color: ${formData.color}`);
  if (formData.material) fields.push(`Material: ${formData.material}`);

  const conditionMap = {
    new: "New with tags",
    "like-new": "Very good",
    excellent: "Very good",
    good: "Good",
    fair: "Satisfactory",
    poor: "Satisfactory",
  };
  const mappedCondition = conditionMap[formData.condition] || "Very good";
  fields.push(`Condition: ${mappedCondition}`);
  fields.push(`Shipping time: 3-5 days`);

  return fields.join("\n");
};

const VintedDetails = ({
  formData,
  vintedConnected,
  vintedMainCategories,
  vintedSubCategories,
  vintedCategoryHistory,
  vintedCurrentCategoryId,
  vintedSelectedCategoryPath,
  vintedLoadingCategories,
  handleVintedCategoryClick,
  handleVintedBack,
  resetVintedCategory,
  handleInputChange,
  vintedDescription,
  onVintedDescriptionChange,
}) => {
  // Ref to track if we have already auto-filled once
  const hasAutoFilled = useRef(false);

  // Auto-generate description only once when component mounts OR when formData fields change,
  // but only if the description is empty and we haven't auto-filled before.
  useEffect(() => {
    if (!vintedDescription && !hasAutoFilled.current) {
      const defaultDesc = getDefaultVintedDescription(formData);
      if (defaultDesc) {
        onVintedDescriptionChange(defaultDesc);
        hasAutoFilled.current = true;
      }
    }
    // We intentionally don't depend on vintedDescription to avoid loops.
    // The effect will run whenever formData fields change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.brand, formData.vintedSize, formData.color, formData.material, formData.condition]);

  // If user manually clears the description, allow auto-fill again on next field change
  useEffect(() => {
    if (!vintedDescription) {
      hasAutoFilled.current = false;
    } else if (vintedDescription) {
      // If user typed something, mark as auto-filled to avoid overwriting
      if (!hasAutoFilled.current && vintedDescription === getDefaultVintedDescription(formData)) {
        // It's the default, keep as is
        hasAutoFilled.current = true;
      } else if (vintedDescription !== getDefaultVintedDescription(formData)) {
        // User edited, prevent auto overwrite
        hasAutoFilled.current = true;
      }
    }
  }, [vintedDescription, formData]);

  return (
    <div
      className="vinted-details-section mt-5 p-4"
      style={{
        border: "1px solid #ddd",
        borderRadius: "12px",
        background: "#f9f9f9",
      }}
    >
      <h4
        className="mb-3"
        style={{
          color: "#7B61FF",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "28px", marginRight: "10px" }}>👕</span>
        Vinted Listing Details
      </h4>

      {/* Vinted Custom Description */}
      <div className="mb-3">
        <label className="form-label fw-bold">Vinted Description (Optional)</label>
        <textarea
          className="form-control"
          rows="4"
          value={vintedDescription || ""}
          onChange={(e) => onVintedDescriptionChange(e.target.value)}
          placeholder="Leave empty to auto-generate from product details (Brand, Size, Color, Material, Condition, Shipping time: 3-5 days)"
        />
        <small className="text-muted">
          This description will be used only on Vinted. If left blank, we will generate a structured description automatically.
        </small>
        <button
          type="button"
          className="btn btn-sm btn-link mt-1"
          onClick={() => {
            const defaultDesc = getDefaultVintedDescription(formData);
            onVintedDescriptionChange(defaultDesc);
            hasAutoFilled.current = true;
          }}
        >
          Reset to default
        </button>
      </div>

      {/* Rest of the component unchanged */}
      <div className="form-group mb-4">
        <label
          style={{
            fontWeight: "600",
            display: "block",
            marginBottom: "10px",
          }}
        >
          Category Selection *
        </label>

        {!formData.vintedCategoryId ? (
          <div
            className="category-selection-container"
            style={{
              border: "2px solid #e0e0e0",
              borderRadius: "8px",
              padding: "15px",
              background: "white",
            }}
          >
            {vintedSelectedCategoryPath.length > 0 && (
              <div
                className="category-breadcrumb"
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "15px",
                  padding: "8px 12px",
                  background: "#f5f5f5",
                  borderRadius: "6px",
                }}
              >
                Categories:{" "}
                {vintedSelectedCategoryPath.map((cat, i) => (
                  <span key={i}>
                    {i > 0 && " › "}
                    <span
                      style={{
                        color: "#7B61FF",
                        fontWeight: 600,
                      }}
                    >
                      {cat}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {vintedCategoryHistory.length > 0 && (
              <button
                className="btn btn-sm btn-light mb-3"
                onClick={handleVintedBack}
                style={{
                  padding: "8px 16px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                }}
              >
                ← Back
              </button>
            )}

            {vintedLoadingCategories ? (
              <div style={{ textAlign: "center", padding: "30px" }}>
                <div
                  style={{
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #7B61FF",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 10px",
                  }}
                ></div>
                <div style={{ color: "#666" }}>Loading categories...</div>
              </div>
            ) : (
              <div
                className="categories-list"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                }}
              >
                {(vintedCurrentCategoryId === null
                  ? vintedMainCategories
                  : vintedSubCategories
                ).map((cat) => (
                  <div
                    key={cat.category_id}
                    className="category-item"
                    onClick={() => handleVintedCategoryClick(cat)}
                    style={{
                      padding: "12px 15px",
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f5f2ff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "white")
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "20px",
                          marginRight: "10px",
                          minWidth: "24px",
                        }}
                      >
                        {getVintedCategoryIcon(cat.name)}
                      </span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{cat.name}</div>
                        {cat.is_final_selection && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#7B61FF",
                            }}
                          >
                            Click to select
                          </div>
                        )}
                      </div>
                    </div>
                    {!cat.is_final_selection && (
                      <span style={{ color: "#999" }}>→</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              background: "#E8F5E9",
              border: "2px solid #7B61FF",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                color: "#2E7D32",
                marginBottom: "5px",
              }}
            >
              Selected: {formData.vintedCategoryPath.join(" > ")}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginBottom: "10px",
              }}
            >
              Category ID: {formData.vintedCategoryId}
            </div>
            <button
              className="btn btn-sm btn-link"
              style={{
                color: "#7B61FF",
                padding: 0,
                border: "none",
                background: "none",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={resetVintedCategory}
            >
              Change Category
            </button>
          </div>
        )}
      </div>

      <div className="form-group mb-4">
        <label
          style={{
            fontWeight: "600",
            display: "block",
            marginBottom: "10px",
          }}
        >
          Package Size *
        </label>
        <div
          className="package-size-container"
          style={{
            border: "2px solid #e0e0e0",
            borderRadius: "8px",
            padding: "15px",
            background: "white",
          }}
        >
          <div
            className={`package-option ${
              formData.vintedPackageSize === "small" ? "selected" : ""
            }`}
            onClick={() => handleInputChange("vintedPackageSize", "small")}
            style={{
              border:
                "2px solid " +
                (formData.vintedPackageSize === "small" ? "#7B61FF" : "#ddd"),
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "10px",
              cursor: "pointer",
              background:
                formData.vintedPackageSize === "small" ? "#F5F2FF" : "white",
            }}
          >
            <div
              style={{
                display: "inline-block",
                background: "#00D4AA",
                color: "white",
                padding: "4px 10px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Recommended
            </div>
            <div
              style={{
                fontWeight: 600,
                fontSize: "16px",
                marginBottom: "5px",
              }}
            >
              Small
            </div>
            <div
              style={{
                color: "#666",
                fontSize: "14px",
                marginBottom: "5px",
              }}
            >
              For items that'd fit in a large envelope.
            </div>
          </div>
          <div
            className={`package-option ${
              formData.vintedPackageSize === "medium" ? "selected" : ""
            }`}
            onClick={() => handleInputChange("vintedPackageSize", "medium")}
            style={{
              border:
                "2px solid " +
                (formData.vintedPackageSize === "medium" ? "#7B61FF" : "#ddd"),
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "10px",
              cursor: "pointer",
              background:
                formData.vintedPackageSize === "medium" ? "#F5F2FF" : "white",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "16px",
                marginBottom: "5px",
              }}
            >
              Medium
            </div>
            <div
              style={{
                color: "#666",
                fontSize: "14px",
                marginBottom: "5px",
              }}
            >
              For items that'd fit in a shoebox.
            </div>
          </div>
          <div
            className={`package-option ${
              formData.vintedPackageSize === "large" ? "selected" : ""
            }`}
            onClick={() => handleInputChange("vintedPackageSize", "large")}
            style={{
              border:
                "2px solid " +
                (formData.vintedPackageSize === "large" ? "#7B61FF" : "#ddd"),
              borderRadius: "8px",
              padding: "15px",
              cursor: "pointer",
              background:
                formData.vintedPackageSize === "large" ? "#F5F2FF" : "white",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "16px",
                marginBottom: "5px",
              }}
            >
              Large
            </div>
            <div
              style={{
                color: "#666",
                fontSize: "14px",
              }}
            >
              For items that'd fit in a moving box.
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mb-3">
        <label
          style={{
            fontWeight: "600",
            display: "block",
            marginBottom: "6px",
          }}
        >
          Product Size (Optional)
        </label>
        <input
          type="text"
          className="form-control"
          placeholder="e.g., M, L, 8, 10"
          value={formData.vintedSize}
          onChange={(e) => handleInputChange("vintedSize", e.target.value)}
          style={{
            padding: "12px",
            borderRadius: "8px",
            border: "2px solid #ddd",
            width: "100%",
          }}
        />
      </div>

      <div
        className="mt-3 p-3"
        style={{
          borderRadius: "8px",
          backgroundColor: vintedConnected ? "#d4edda" : "#fff3cd",
          color: vintedConnected ? "#155724" : "#856404",
          border: `1px solid ${vintedConnected ? "#c3e6cb" : "#ffeeba"}`,
        }}
      >
        <strong>Vinted Extension Status:</strong>{" "}
        {vintedConnected
          ? "Connected - Ready to list!"
          : "Not connected - Click 'Integrate' button above"}
      </div>
    </div>
  );
};

export default VintedDetails;